import { format } from 'date-fns';
import axiosInstance from '../../utils/axios';
import { getAppDetails } from '../../utils/terminal';
import { InvoiceData, RejectedTicket, StatModel, Ticket, TicketDetails } from './models';
import actionTypes from './types';
import { createAsyncThunkWithRetry } from '../../utils/asyncThunkWithRetry';

export const fetchTickets = createAsyncThunkWithRetry(actionTypes.FETCH_TICKETS, async (currentTickets: Ticket[]) => {
  try {
    const appDetails = await getAppDetails();
    const response = await axiosInstance.get<Ticket[]>(`/v1/paybox/${appDetails.id}/tickets/available`);
    //console.log('Received available tickets: ' + JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    console.error(JSON.stringify(error));
    return currentTickets;
  }
});

export const fetchStats = createAsyncThunkWithRetry(actionTypes.FETCH_STATS, async () => {
  try {
    const appDetails = await getAppDetails();
    const response = await axiosInstance.get<StatModel[]>(`/v1/paybox/${appDetails.id}/tickets/availability-pools`);
    return response.data;
  } catch (error) {
    console.error(JSON.stringify(error));
    return [] as StatModel[];
  }
});

export const getTicketDetails = createAsyncThunkWithRetry(actionTypes.FETCH_TICKET_DETAILS, async (ticketId: string): Promise<TicketDetails | null> => {
  try {
    const appDetails = await getAppDetails();
    const response = await axiosInstance.get<TicketDetails>(`/v1/paybox/${appDetails.id}/tickets/${ticketId}/details`);
    return response.data;
  } catch (error) {
    console.error(JSON.stringify(error));
    return null;
  }
});

export const getTicketsByPlates = createAsyncThunkWithRetry(actionTypes.FETCH_TICKET_BY_PLATES, async (plates: string): Promise<TicketDetails[] | null> => {
  try {
    const appDetails = await getAppDetails();
    const response = await axiosInstance.get<TicketDetails[]>(
      `/v1/paybox/${appDetails.id}/tickets/search?plates=${plates}`
    );
    return response.data;
  } catch (error) {
    console.error(JSON.stringify(error));
    return null;
  }
});

export const getInvoiceDataFromKrs = createAsyncThunkWithRetry(actionTypes.GET_INVOICE_DATA_FROM_KRS, async (nip: string): Promise<InvoiceData | null> => {
  try {
    const appDetails = await getAppDetails();
    const response = await axiosInstance.get<InvoiceData>(
      `/v1/paybox/${appDetails.id}/tickets/invoice-data?nip=${nip}`
    );
    return response.data;
  } catch (error) {
    console.error(JSON.stringify(error));
    return null;
  }
});

export const saveAnswer = async (
  ticketId: string,
  answer: string,
  orderId: string,
  questionAnswerId: number
): Promise<void> => {
  try {
    const appDetails = await getAppDetails();
    const data = JSON.stringify({
      orderId: orderId,
      question: {
        questionAnswerId: questionAnswerId,
        answer: answer,
      },
    });
    await axiosInstance.put(`/v1/paybox/${appDetails.id}/tickets/${ticketId}/question`, data);
  } catch (error) {
    console.error(JSON.stringify(error));
  }
};

export const markTicketAsUsed = createAsyncThunkWithRetry(actionTypes.MARK_TICKET_AS_USED, async (ticketId: string): Promise<boolean> => {
  try {
    const appDetails = await getAppDetails();
    await axiosInstance.put(`/v1/paybox/${appDetails.id}/tickets/${ticketId}/complete-used`);
    return true;
  } catch (error) {
    console.error(JSON.stringify(error));
    return false;
  }
});

export const rejectTicket = createAsyncThunkWithRetry(actionTypes.REJECT_TICKET, async (ticketId: string): Promise<RejectedTicket | null> => {
  try {
    const appDetails = await getAppDetails();
    const response = await axiosInstance.put<RejectedTicket>(`/v1/paybox/${appDetails.id}/tickets/${ticketId}/reject`);
    return response.data;
  } catch (error) {
    console.error(JSON.stringify(error));
    return null;
  }
});

export const extendTicket = createAsyncThunkWithRetry(actionTypes.EXTEND_TICKET, async (ticketId: string): Promise<boolean> => {
  try {
    const appDetails = await getAppDetails();
    const data = JSON.stringify({
      newDate: format(new Date(), 'yyyy-MM-dd'),
    });
    await axiosInstance.put(`/v1/paybox/${appDetails.id}/tickets/${ticketId}/extend`, data);
    return true;
  } catch (error) {
    console.error(JSON.stringify(error));
    return false;
  }
});
